import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import logo from '../assets/images/logo.svg';
import { useAuth } from '../contexts/AuthContext';
import { useTranslations } from '../hooks/useTranslations';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState(null);
  const navigate = useNavigate();
  const form = useRef(null);
  const { login } = useAuth();
  const { t } = useTranslations();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') {
      setEmail(value);
    } else if (name === 'password') {
      setPassword(value);
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch('https://api.maxprofitacademy.com/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        await login(data.token);
        navigate('/dashboard');
      } else {
        setError(data.message || t('login.loginError'));
      }
    } catch (err) {
      setError(t('login.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetStatus(null);
    setIsLoading(true);

    try {
      const response = await fetch('https://api.maxprofitacademy.com/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setResetStatus({
          type: 'success',
          message: t('login.resetSuccess')
        });
        setTimeout(() => {
          setShowResetModal(false);
          setResetEmail('');
          setResetStatus(null);
        }, 3000);
      } else {
        setResetStatus({
          type: 'error',
          message: data.message || t('login.resetError')
        });
      }
    } catch (err) {
      setResetStatus({
        type: 'error',
        message: t('login.serverError')
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left side - Decoration */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-custom"
        />
        <div className="absolute inset-x-0 bottom-24 p-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-black mb-4">
              {t('login.welcomeTitle')}
            </h3>
            <p className="text-black/90 max-w-md mx-auto">
              {t('login.welcomeSubtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <a href="https://maxprofitacademy.com">
              <img src={logo} alt="MaxProfit Academy" className="h-12 mx-auto mb-6" />
            </a>
            <h2 className="text-2xl font-bold text-gray-900">{t('login.loginTitle')}</h2>
            {/* <p className="text-gray-600 mt-2">Введите ваши данные для входа</p> */}
          </div>

          <form ref={form} onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={handleChange}
                placeholder={t('login.emailPlaceholder')}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                required
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={handleChange}
                placeholder={t('login.passwordPlaceholder')}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              className="w-full py-3 px-4 bg-primary text-black font-medium rounded-2xl hover:bg-primary/90 transition-colors"
            >
              {t('login.loginButton')}
            </button>

            <div className="text-center mt-6">
              <Link
                to="/register"
                className="text-gray-600 hover:text-primary transition-colors underline"
              >
                {t('login.noAccount')}
              </Link>
            </div>
            <div className="text-center mt-2">
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-gray-600 hover:text-primary transition-colors underline"
              >
                {t('login.forgotPassword')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowResetModal(false)} />

            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => setShowResetModal(false)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                    {t('login.resetPasswordTitle')}
                  </h3>
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <input
                        type="email"
                        id="resetEmail"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-2xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                        placeholder={t('login.resetEmailPlaceholder')}
                        required
                      />
                    </div>

                    {resetStatus && (
                      <div className={`mt-2 text-sm ${
                        resetStatus.type === 'success' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {resetStatus.message}
                      </div>
                    )}

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:ml-3 sm:w-auto"
                      >
                        {isLoading ? t('login.sending') : t('login.resetPasswordButton')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowResetModal(false);
                          setResetEmail('');
                          setResetStatus(null);
                        }}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      >
                        {t('login.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login; 